import { useIsRestoring, useQueryClient } from '@tanstack/react-query'
import { useEffect, useSyncExternalStore } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { BrandLoader } from '@/components/brand-loader'
import { Button } from '@/components/ui/button'
import { forgetServerState } from '@/lib/query-client'
import { appPersister } from '@/lib/query-persist'
import { isBootRouteReady, subscribeBootRoute } from '@/lib/route-chunks'
import { currentPath, loginUrlFor } from './returnTo'
import { useAuth } from './useAuth'

/**
 * Route guard: renders child routes only when signed in, else bounces to /login.
 *
 * A user restored from the persisted cache is not trusted until one /auth/me in
 * this page's life names the same id (useAuth): until then it shows the brand
 * loader, however long the check takes, so a page restored for A is never drawn
 * after an OAuth sign-in as B. A "signed out" answer clears everything on the
 * way to /login. While the cache is still being read back from disk it shows the
 * brand loader too — never the login form, which a restore about to find a
 * session would only flash.
 *
 * It also holds that loader until the page in the address bar has its code
 * (preloaded at boot, capped — lib/route-chunks.ts `preloadBootRoute`): a
 * refresh is one loader, then the whole page, never the chrome around a second
 * loader while the page's chunk is still on its way.
 */
export function RequireAuth() {
  const isRestoring = useIsRestoring()
  const { isAuthenticated, isLoading, isUnknown, isRechecking, recheck } = useAuth()
  const location = useLocation()
  const pageReady = useSyncExternalStore(subscribeBootRoute, isBootRouteReady)

  // A signed-out visitor is not kept waiting on the code of a page they will not see.
  if (isRestoring || isLoading || (isAuthenticated && !pageReady)) {
    // It hands over rather than cutting: it fades out on top of the page as the
    // page arrives underneath (lib/loader-handoff.ts).
    return <BrandLoader size="page" handoff />
  }
  // We could not ASK whether they are signed in. Bouncing to /login here tells
  // a signed-in person they are signed out and makes them re-authenticate for
  // a network blip — offer the question again instead.
  if (isUnknown) {
    return (
      <div className="grid min-h-svh place-items-center gap-3 p-6 text-center">
        <p className="text-muted-foreground">We could not check your session.</p>
        <Button variant="outline" disabled={isRechecking} onClick={() => void recheck()}>
          {isRechecking ? 'Checking…' : 'Try again'}
        </Button>
      </div>
    )
  }
  if (!isAuthenticated) {
    // Carry where they were going, query string included. Bouncing to a bare
    // /login loses it, which for a page whose whole content is in the query
    // string — a one-use OAuth verifier, say — throws the trip away.
    return <SignedOut to={loginUrlFor(currentPath(location))} />
  }
  return <Outlet />
}

/** The session is gone: forget every cached answer, then go to /login. */
function SignedOut({ to }: { to: string }) {
  const client = useQueryClient()
  useEffect(() => {
    void forgetServerState(client, appPersister)
  }, [client])
  return <Navigate to={to} replace />
}
